import * as THREE from "three";
import AddExtrudedLoopAnnotationCmd from "./AddExtrudedLoopAnnotationCmd";
import CommandIdEnum from "../enums/CommandIdEnums";
import ICanvasInputListener from "../logic/ICanvasInputListener";
import Document from "../model/document/Document";
import CommandStack from "../logic/CommandStack";
import Placement3d from "../model/Placement3d";
import RectangleAnnotationData from "../model/sceneObjectData/RectangleAnnotationData";
import RectangleCornerHintHelper from "./commandHelpers/RectangleCornerHintHelper";
import { SceneManager } from "../model/SceneManager";
import EditHandleTypeEnum from "../enums/EditHandleTypeEnum";

export default class AddRectangleAnnotationCmd extends AddExtrudedLoopAnnotationCmd<RectangleAnnotationData> {

    #cornerHint = new RectangleCornerHintHelper();

    constructor(commandStack: CommandStack, canvasInputListener: ICanvasInputListener, document: Document) {
        super(CommandIdEnum.AddRectangleAnnotation, commandStack, canvasInputListener, document);
        this.commandNameResourceKey = 'command.addRectangleAnnotation.title';
        this.initialPrompResourceKey = 'command.addRectangleAnnotation.selectFirstPoint';
        this.nextPointPromptResourceKey = 'command.addRectangleAnnotation.selectSecondPoint';
        this.#cornerHint.addToScene(SceneManager.scene);
    }

    protected createInitialData(placement: Placement3d, depth: number): RectangleAnnotationData {
        return new RectangleAnnotationData(placement, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 0), depth);
    }

    protected onSubsequentPoint(data: RectangleAnnotationData, alignedPoint: THREE.Vector3): 'continue' | 'showFinish' | 'autoFinish' {
        data.corner2 = alignedPoint.clone().sub(data.placement.location);
        data.applyAreaBasedFontSize();
        data.applyAreaBasedShowAreaText();
        return 'autoFinish';
    }

    protected buildPreviewData(data: RectangleAnnotationData, alignedPoint: THREE.Vector3): RectangleAnnotationData {
        const preview = data.clone();
        preview.corner2 = alignedPoint.clone().sub(data.placement.location);
        preview.applyAreaBasedFontSize();
        return preview;
    }

    protected creationMeasurementHandleType(): EditHandleTypeEnum {
        return EditHandleTypeEnum.RECTANGLE_CORNER;
    }

    protected getRefAxisPoints(data: RectangleAnnotationData): THREE.Vector3[] {
        return [
            data.placement.location.clone().add(data.corner1),
            data.placement.location.clone().add(data.corner2),
        ];
    }

    // Rejects an exact-zero-area double-click on the same spot: the command stays open (this.data
    // is already set), so the very next click simply retries setting corner2.
    protected isReadyToFinish(data: RectangleAnnotationData): boolean {
        return data.computeEnclosedArea() > 1e-9;
    }

    protected finishButtonResourceKey(): string {
        return 'command.addRectangleAnnotation.finish';
    }

    protected updatePointHint(point: THREE.Vector3, normal: THREE.Vector3): void {
        const refDirection = this.deriveRefDirection(normal);
        this.#cornerHint.update(point, normal, refDirection);
        this.#cornerHint.show();
    }

    protected hidePointHint(): void {
        this.#cornerHint.hide();
    }

    dispose(): void {
        this.#cornerHint.dispose();
        super.dispose();
    }
}
